import type { TelemetryVital } from '@/apis/telemetry';
import type { ShareLinkSettings } from '@/apis/types';

/** Vitals chip helpers for the shipment drawer, table and map. */

export type VitalModalType = TelemetryVital;

export const VITAL_ORDER: VitalModalType[] = [
  'temperature',
  'battery',
  'shock_value',
  'light',
  'humidity',
  'tilt',
  'pressure',
  'cell_signal',
];

type ReadingSource = Partial<
  Record<VitalModalType, string | number | null | undefined>
>;

type SensorSummaryEntry = {
  sensor_id?: string;
  available_vitals?: string[];
};

/** Vitals with data for one sensor, in chip order; the same "has data" rule as the chips (hasVitalData). */
export function getAvailableVitalsForSensor(
  sensorData: { sensorId?: string; latestReading: ReadingSource | null } | null,
  shipment: {
    monitor_info?: ReadingSource | ReadingSource[] | null;
    sensor_summary?: SensorSummaryEntry[] | null;
  }
): VitalModalType[] {
  return VITAL_ORDER.filter((type) =>
    hasVitalData(type, { shipment, sensorData })
  );
}

/** Vitals a share link hides: all of them when its sensor data is off. Empty off share links. */
export function getShareHiddenVitals(
  shipment?: {
    share_settings?: ShareLinkSettings;
  } | null
): VitalModalType[] {
  const settings = shipment?.share_settings;
  if (!settings) return [];
  if (!settings.show_sensor_data) return [...VITAL_ORDER];
  return settings.hidden_vitals ?? [];
}

/**
 * Share links with sensor data off keep the route but not its sensor reading
 * points: the map shows only the latest position, and no point opens a card.
 */
export function shareHidesSensorPoints(
  shipment?: {
    share_settings?: ShareLinkSettings;
  } | null
): boolean {
  return shipment?.share_settings?.show_sensor_data === false;
}

/** Tooltip label for a vital tag. */
export const VITAL_TAG_LABELS: Record<VitalModalType, string> = {
  temperature: 'Temperature sensor reading',
  battery: 'Sensor battery life',
  light: 'Light sensor reading',
  humidity: 'Humidity sensor reading',
  tilt: 'Tilt sensor reading',
  pressure: 'Pressure sensor reading',
  shock_value: 'Shock sensor reading',
  cell_signal: 'Cell signal sensor reading',
};

/** Vitals that are always shown as tags (gray when no data). */
export const ALWAYS_SHOW_VITALS: VitalModalType[] = ['temperature', 'battery'];

/** Display unit for vital tag. Pressure uses product.pressure_unit. */
export function getVitalDisplayUnit(
  type: VitalModalType,
  shipment?: { product?: { pressure_unit?: string } } | null
): string {
  if (type === 'pressure') return shipment?.product?.pressure_unit ?? '';
  const units: Record<VitalModalType, string> = {
    temperature: '°C',
    battery: '%',
    light: ' lx',
    humidity: '%',
    tilt: '°',
    pressure: '',
    shock_value: ' G',
    cell_signal: '%',
  };
  return units[type];
}

/** Format a vital value for display: whole number or '--' if missing/invalid. */
export function formatVitalDisplay(
  value: string | number | null | undefined,
  fallback = '--',
  precision = 0
): string | number {
  if (value == null || value === '') return fallback;
  const n = Number(value);
  if (Number.isNaN(n)) return fallback;
  if (precision <= 0) return Math.round(n);
  return n.toFixed(precision);
}

/** Tag style for vital display (background + text color). */
export interface TagStyles {
  backgroundColor: string;
  color: string;
}

const STYLES = {
  gray: {
    backgroundColor: 'var(--light-gray)',
    color: 'var(--sensor-neutral-text)',
  },
  red: {
    backgroundColor: 'var(--danger-thin)',
    color: 'var(--sensor-danger-text)',
  },
  orange: { backgroundColor: 'var(--light-warning)', color: 'var(--warning)' },
  green: { backgroundColor: 'var(--thin-green)', color: 'var(--green-900)' },
} as const;

/** Tag styles for range vitals (temp, humidity, pressure, light, tilt). */
export function getTagStyles(
  min: number | string | undefined,
  max: number | string | undefined,
  current: number | string | undefined
): TagStyles {
  if (current == null || current === '') return STYLES.gray;
  const num = Number(current);
  const numMin = typeof min === 'number' ? min : undefined;
  const numMax = typeof max === 'number' ? max : undefined;

  if (numMin !== undefined && numMax !== undefined) {
    const range = numMax - numMin;
    const lower = numMin + range * 0.2;
    const upper = numMax - range * 0.2;
    if (num < numMin || num > numMax) return STYLES.red;
    if (num < lower || num > upper) return STYLES.orange;
    return STYLES.green;
  }

  const refMax = numMax ?? numMin;
  if (typeof refMax === 'number') {
    const eighty = refMax * 0.8;
    if (num >= refMax) return STYLES.red;
    if (num >= eighty) return STYLES.orange;
  }
  return STYLES.green;
}

/** Tag styles for battery (%, thresholds at 10 and 20). */
export function getBatteryTagStyles(
  battery: number | string | undefined
): TagStyles {
  if (battery == null || battery === '') return STYLES.gray;
  const num = Number(battery);
  if (num <= 10) return STYLES.red;
  if (num <= 20) return STYLES.orange;
  return STYLES.green;
}

/** Tag styles for cell signal (%, thresholds at 10 and 25). */
export function getCellSignalTagStyles(
  signal: number | string | undefined
): TagStyles {
  if (signal == null || signal === '') return STYLES.gray;
  const num = Number(signal);
  if (num <= 10) return STYLES.red;
  if (num <= 25) return STYLES.orange;
  return STYLES.green;
}

/** Lit rings on the signal icon: none to 10 and one to 25 (the tag colour's cut-offs), two to 50, three above. */
export function getCellSignalRings(
  signal: number | string | undefined
): 0 | 1 | 2 | 3 {
  if (signal == null || signal === '') return 0;
  const num = Number(signal);
  if (num <= 10) return 0;
  if (num <= 25) return 1;
  if (num <= 50) return 2;
  return 3;
}

/** Minimal product shape for tag style (min/max limits). */
export type VitalProductLimits = {
  temperature_min?: number;
  temperature_max?: number;
  humidity_min?: number;
  humidity_max?: number;
  pressure_min?: number;
  pressure_max?: number;
  light?: number;
  tilt?: number;
  pressure_unit?: string;
};

/** Tag style for a vital type given shipment limits and a getValue function. */
export function getTagStyleForVital(
  vitalType: VitalModalType,
  product: VitalProductLimits | undefined,
  getValue: (field: VitalModalType) => string | number | undefined
): TagStyles {
  switch (vitalType) {
    case 'temperature':
      return getTagStyles(
        product?.temperature_min,
        product?.temperature_max,
        getValue('temperature')
      );
    case 'battery':
      return getBatteryTagStyles(getValue('battery'));
    case 'light':
      return getTagStyles('', product?.light, getValue('light'));
    case 'humidity':
      return getTagStyles(
        product?.humidity_min,
        product?.humidity_max,
        getValue('humidity')
      );
    case 'tilt':
      return getTagStyles('', product?.tilt, getValue('tilt'));
    case 'pressure':
      return getTagStyles(
        product?.pressure_min,
        product?.pressure_max,
        getValue('pressure')
      );
    case 'shock_value':
      return getTagStyles('', undefined, getValue('shock_value'));
    case 'cell_signal':
      return getCellSignalTagStyles(getValue('cell_signal'));
    default:
      return STYLES.green;
  }
}

/** Generic reading source (monitor point, monitor_info, pointState). */
export type VitalReadingSource = Partial<
  Record<VitalModalType | 'lat_updated', string | number | null | undefined>
>;

/** Gets latest value for a vital from shipment data. Handles single/multi-sensor + pointState. */
export function getLatestVitalValue(
  field: VitalModalType,
  sources: {
    shipment: {
      monitor_info?:
        | (VitalReadingSource & { shipment_sensor_id?: string })
        | (VitalReadingSource & { shipment_sensor_id?: string })[]
        | null;
    };
    sensorData: { sensorId?: string } | null;
    pointState?: VitalReadingSource | null;
    isPrimary?: boolean;
  }
): string | number | undefined {
  const { shipment, sensorData, pointState, isPrimary } = sources;

  const usePointState = isPrimary && pointState?.lat_updated;
  if (usePointState) {
    const v = pointState[field];
    if (v != null && v !== '') return v;
  }

  const info = shipment?.monitor_info;
  if (info) {
    const infoPoints = Array.isArray(info) ? info : [info];
    const match = sensorData?.sensorId
      ? infoPoints.find((p) => p.shipment_sensor_id === sensorData.sensorId)
      : infoPoints[0];
    const v = match?.[field];
    if (v != null && v !== '') return v;
  }
  return undefined;
}

/** True if any reading for this sensor has the vital field. Handles single/multi-sensor. */
export function hasVitalData(
  field: VitalModalType,
  sources: {
    shipment: {
      sensor_summary?: SensorSummaryEntry[] | null;
      monitor_info?:
        | (VitalReadingSource & { shipment_sensor_id?: string })
        | (VitalReadingSource & { shipment_sensor_id?: string })[]
        | null;
    };
    sensorData: { sensorId?: string } | null;
  }
): boolean {
  const { shipment, sensorData } = sources;
  const summary = shipment?.sensor_summary ?? [];
  if (summary.length > 0) {
    if (sensorData?.sensorId) {
      const s = summary.find((x) => x.sensor_id === sensorData.sensorId);
      return Boolean(s?.available_vitals?.includes(field));
    }
    return summary.some((x) => x.available_vitals?.includes(field));
  }

  const check = (p: VitalReadingSource) => {
    const v = p[field];
    return v != null && v !== '';
  };
  const info = shipment?.monitor_info;
  if (info) {
    const points = Array.isArray(info) ? info : [info];
    if (sensorData?.sensorId) {
      const match = points.find(
        (p) => p.shipment_sensor_id === sensorData.sensorId
      );
      return match ? check(match) : false;
    }
    return points.some((p) => check(p));
  }
  return false;
}
